import React, { memo, useCallback, useMemo, useState } from 'react';
import { User, Bot, ThumbsUp, ThumbsDown, Copy, Image, FileText, File, MessageSquareText } from 'lucide-react';
import { Message as MessageType, FileItem } from '../../types';
import StatusSteps from './StatusSteps';
import VerdictCard from './VerdictCard';

interface MessageProps {
    message: MessageType;
    onLike?: (id: number, liked: boolean | null) => void;
    onCopy?: (text: string) => void;
    onFeedback?: (id: number, text: string) => void;
}

const Message: React.FC<MessageProps> = memo(({ message, onLike, onCopy, onFeedback }) => {
    // 2026-09-18 用户令：每条 AI 回复带意见反馈入口（赞/踩/复制之外第 4 钮）
    const [fbOpen, setFbOpen] = useState(false);
    const [fbText, setFbText] = useState('');
    console.log(`🎨 渲染消息 ${message.id}, 内容长度: ${message.text.length}`);
    const renderText = (text: string) => {
        if (!text) return '';
        return text.split('\n').map((line, i) => (
            <React.Fragment key={i}>
                {line}
                {i < text.split('\n').length - 1 && <br />}
            </React.Fragment>
        ));
    };
    const formattedText = renderText(message.text);
    const verdictCard = (message.sender === 'ai' && (message.verdict || message.reviewNote || message.partnerNote))
        ? <VerdictCard verdict={message.verdict} reviewNote={message.reviewNote} partnerNote={message.partnerNote} />
        : null;
    

    const formatFileSize = useCallback((bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }, []);

    const getFileIcon = useCallback((type: string | undefined | null) => {
        if (!type) return <File size={16} />;
        if (type.startsWith('image/')) return <Image size={16} />;
        if (type.includes('pdf')) return <FileText size={16} />;
        return <File size={16} />;
    }, []);

    const handleLike = useCallback((liked: boolean | null) => {
        if (onLike) {
            onLike(message.id, liked);
        }
    }, [message.id, onLike]);

    const handleCopy = useCallback(() => {
        if (onCopy) {
            onCopy(message.text);
        }
    }, [message.text, onCopy]);

    const handleFeedbackSubmit = useCallback(() => {
        const t = fbText.trim();
        if (!t || !onFeedback) return;
        onFeedback(message.id, t);
        setFbText('');
        setFbOpen(false);
    }, [fbText, message.id, onFeedback]);

    // 点击消息附件 → 预览（与离线版一致：window.open 新窗口打开）
    const previewMessageFile = useCallback((file: FileItem) => {
        const url = file.preview || URL.createObjectURL(file.file);
        window.open(url, '_blank');
    }, []);

    const messageFiles = useMemo(() => {
        if (!message.files || message.files.length === 0) return null;

        return (
            <div className="message-files">
                {message.files.map(file => (
                    <div
                        key={file.id}
                        className="message-file-item message-file-clickable"
                        onClick={() => previewMessageFile(file)}
                        title={`点击预览：${file.name}`}
                    >
                        <div className="message-file-icon">
                            {getFileIcon(file.type)}
                        </div>
                        <span className="message-file-name">
                            {file.name} ({formatFileSize(file.size)})
                        </span>
                    </div>
                ))}
            </div>
        );
    }, [message.files, getFileIcon, formatFileSize, previewMessageFile]);

    const actionButtons = useMemo(() => {
        if (message.sender !== 'ai') return null;

        return (
            <div className="message-actions">
                <button
                    className={`action-btn ${message.liked === true ? 'active' : ''}`}
                    onClick={() => handleLike(message.liked === true ? null : true)}
                    title="赞"
                >
                    <ThumbsUp size={16} />
                </button>
                <button
                    className={`action-btn ${message.liked === false ? 'active' : ''}`}
                    onClick={() => handleLike(message.liked === false ? null : false)}
                    title="踩"
                >
                    <ThumbsDown size={16} />
                </button>
                <button
                    className="action-btn"
                    onClick={handleCopy}
                    title="复制"
                >
                    <Copy size={16} />
                </button>
                <button
                    className={`action-btn ${fbOpen || message.feedbackText ? 'active' : ''}`}
                    onClick={() => setFbOpen(v => !v)}
                    title={message.feedbackText ? '已反馈（点击再补充）' : '反馈意见'}
                >
                    <MessageSquareText size={16} />
                </button>
            </div>
        );
    }, [message.sender, message.liked, message.feedbackText, fbOpen, handleLike, handleCopy]);

    return (
        <div className={`message ${message.sender === 'user' ? 'user-message' : 'ai-message'}`}>
            <div className="message-avatar">
                {message.sender === 'user' ? <User size={20} /> : <Bot size={20} />}
            </div>
            <div className="message-content">
                <div className="message-header">
                    <span className="sender-name">
                        {message.sender === 'user' ? 'You' : 'NTRK审核助手'}
                    </span>
                    <span className="timestamp">{message.timestamp}</span>
                </div>
                <div className="message-text">
                    {verdictCard}
                    {formattedText}
                </div>

                {message.steps && message.steps.length > 0 && (
                    <StatusSteps
                        steps={message.steps}
                        startedAt={message.stepStartedAt}
                        doneAt={message.stepsDoneAt}
                        onCancel={message.onCancel}
                    />
                )}
                {message.artifacts && message.artifacts.length > 0 && (
                    <div className="msg-artifacts" style={{ marginTop: 6 }}>
                        {message.artifacts.map((a, i) => (
                            <details key={i} style={{ margin: '4px 0', padding: 6, border: '1px solid #e0e0e0', borderRadius: 6, background: '#fafafa' }}>
                                <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#555', fontSize: 13 }}>{a.title}</summary>
                                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: '8px 0 4px', fontSize: 13, maxHeight: 400, overflowY: 'auto' }}>{a.content}</pre>
                            </details>
                        ))}
                    </div>
                )}
                {message.notice && (
                    <div className="message-notice">{message.notice}</div>
                )}

                {messageFiles}
                {actionButtons}
                {message.sender === 'ai' && fbOpen && (
                    <div className="feedback-box" style={{ marginTop: 6, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                        <textarea
                            value={fbText}
                            onChange={e => setFbText(e.target.value)}
                            placeholder="对这条回复的意见（如：识别有误、建议补充……），将反馈给后台"
                            rows={2}
                            style={{ flex: 1, fontSize: 13, padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, resize: 'vertical' }}
                        />
                        <button className="action-btn" onClick={handleFeedbackSubmit} title="提交反馈"
                                style={{ padding: '6px 10px', fontSize: 13 }}>提交</button>
                        <button className="action-btn" onClick={() => setFbOpen(false)} title="取消"
                                style={{ padding: '6px 10px', fontSize: 13 }}>取消</button>
                    </div>
                )}
                {message.sender === 'ai' && message.feedbackText && !fbOpen && (
                    <div style={{ marginTop: 4, fontSize: 12, color: '#10b981' }}>✓ 已反馈：{message.feedbackText}</div>
                )}
            </div>
        </div>
    );
});

Message.displayName = 'Message';

export default Message;